import React, { useState, useEffect } from "react";
import useApi from "../../../CustomHooks/useApi";
import { ArrowLeft, Building2, Tag, Globe, CreditCard, CheckCircle, ChevronRight, Info } from "lucide-react";

const BillerInfo = ({ billerId, billerName, onBack, onProceed }) => {
    const [billerInfo, setBillerInfo] = useState(null);
    const [loading, setLoading] = useState(false);

    const { callApi: getBillerInfo } = useApi(
        "/bbps/biller-info",
        "POST"
    );

    const fetchBillerInfo = async () => {
        try {
            setLoading(true);

            const payload = {
                billerIds: [billerId],
            };

            const response = await getBillerInfo(payload);

            console.log("Biller Info Response:", response);

            if (response?.success) {
                setBillerInfo(response.data);
                onProceed(response.data);
            }
        } catch (error) {
            console.error("Biller Info Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (billerId) {
            fetchBillerInfo();
        }
    }, [billerId]);

    const info = Array.isArray(billerInfo) ? billerInfo[0] : billerInfo;

    const DetailRow = ({ label, value }) => {
        if (!value) return null;
        return (
            <div className="flex justify-between items-start py-2.5 border-b border-slate-50 last:border-0">
                <span className="text-xs text-slate-400 font-medium w-36 shrink-0">{label}</span>
                <span className="text-xs text-slate-700 font-semibold text-right">{value}</span>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-slate-50">

            {/* Header */}
            <div className="bg-white border-b px-6 py-4 flex items-center gap-4">
                <button
                    onClick={onBack}
                    className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                    <ArrowLeft size={16} /> Back
                </button>
                <div className="h-5 w-px bg-slate-200" />
                <div>
                    <h1 className="text-base font-bold text-slate-800">{billerName || "Biller Info"}</h1>
                    <p className="text-xs text-slate-400">Loading biller details...</p>
                </div>
            </div>

            <div className="p-6 max-w-3xl mx-auto">

                {/* Loading state */}
                {loading && (
                    <div className="bg-white rounded-2xl shadow-sm border p-10 flex flex-col items-center justify-center gap-4">
                        <div className="w-12 h-12 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin" />
                        <p className="text-sm text-slate-500 font-medium">Fetching biller information...</p>
                    </div>
                )}

                {!loading && info && (
                    <div className="space-y-4">

                        {/* Biller Identity Card */}
                        <div className="bg-white rounded-2xl shadow-sm border p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                                    <Building2 size={16} className="text-blue-600" strokeWidth={1.8} />
                                </div>
                                <h2 className="text-sm font-semibold text-slate-700">Biller Details</h2>
                            </div>
                            <DetailRow label="Biller ID" value={info.billerId} />
                            <DetailRow label="Biller Name" value={info.billerName} />
                            <DetailRow label="Category" value={info.billerCategory} />
                            <DetailRow label="Coverage" value={info.billerCoverage} />
                            <DetailRow label="Type" value={info.billerType} />
                            <DetailRow label="Mode" value={info.billerMode} />
                        </div>

                        {/* Payment Info */}
                        {(info.supportedPaymentModes || info.paymentAmountExactness || info.customerConvenienceFee) && (
                            <div className="bg-white rounded-2xl shadow-sm border p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                                        <CreditCard size={16} className="text-green-600" strokeWidth={1.8} />
                                    </div>
                                    <h2 className="text-sm font-semibold text-slate-700">Payment Info</h2>
                                </div>
                                {(() => {
                                    const raw = info.supportedPaymentModes;
                                    const all = Array.isArray(raw)
                                        ? raw.map(m => (typeof m === "object" ? m.paymentMode : m))
                                        : raw ? [raw] : [];
                                    const allowed = all.filter(m =>
                                        typeof m === "string" && /^(Cash|Wallet)$/i.test(m.trim())
                                    );
                                    const display = allowed.length > 0
                                        ? allowed.join(", ")
                                        : "Not Supported: No Payment Method Available.";
                                    return <DetailRow label="Payment Modes" value={display} />;
                                })()}
                                <DetailRow label="Amount Exactness" value={info.paymentAmountExactness} />
                                <DetailRow label="Convenience Fee" value={info.customerConvenienceFee} />
                                <DetailRow label="Min Amount" value={info.paymentAmountMinimum ? `₹${info.paymentAmountMinimum}` : null} />
                                <DetailRow label="Max Amount" value={info.paymentAmountMaximum ? `₹${info.paymentAmountMaximum}` : null} />
                            </div>
                        )}

                        {/* Input Parameters */}
                        {Array.isArray(info.billerInputParams) && info.billerInputParams.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-sm border p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                                        <Info size={16} className="text-purple-600" strokeWidth={1.8} />
                                    </div>
                                    <h2 className="text-sm font-semibold text-slate-700">Required Input Fields</h2>
                                </div>
                                <div className="space-y-2">
                                    {info.billerInputParams.map((param, idx) => (
                                        <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                                            <div>
                                                <p className="text-sm font-semibold text-slate-700">{param.paramName}</p>
                                                <p className="text-xs text-slate-400 mt-0.5">
                                                    {param.dataType} {param.minLength && param.maxLength ? `· ${param.minLength}–${param.maxLength} chars` : ""}
                                                </p>
                                            </div>
                                            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${param.optional === "true" || param.optional === true ? "bg-slate-100 text-slate-500" : "bg-blue-100 text-blue-600"}`}>
                                                {param.optional === "true" || param.optional === true ? "Optional" : "Required"}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Additional Info */}
                        {(info.fetchRequirement || info.billerDescription || info.billerAdhoc) && (
                            <div className="bg-white rounded-2xl shadow-sm border p-5">
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                                        <Globe size={16} className="text-amber-600" strokeWidth={1.8} />
                                    </div>
                                    <h2 className="text-sm font-semibold text-slate-700">Additional Info</h2>
                                </div>
                                <DetailRow label="Fetch Requirement" value={info.fetchRequirement} />
                                <DetailRow label="Adhoc" value={info.billerAdhoc} />
                                {info.billerDescription && (
                                    <p className="text-xs text-slate-500 mt-2">{info.billerDescription}</p>
                                )}
                            </div>
                        )}

                        {/* Proceed Button */}
                        <button
                            onClick={() => onProceed(billerInfo)}
                            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-2xl transition-colors text-sm"
                        >
                            <CheckCircle size={18} />
                            Proceed to Fetch Bill
                            <ChevronRight size={16} />
                        </button>
                    </div>
                )}

                {!loading && !info && (
                    <div className="bg-white rounded-2xl shadow-sm border p-10 text-center text-slate-400">
                        <Building2 size={40} className="mx-auto mb-3 text-slate-200" />
                        <p className="text-sm">No biller information available.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BillerInfo;